/**
 * Cache Helper Utilities
 * Provides utilities for updating cache after mutations
 */

import * as cache from './localCache';

/**
 * Normalize entity data
 */
const normalizeEntity = (entity) => {
  if (!entity) return null;
  if (!entity._id && !entity.id) return null;

  return {
    ...entity,
    updatedAt: entity.updatedAt || entity.createdAt || new Date().toISOString(),
  };
};

/**
 * Update cache after creating an entity
 */
export const cacheCreate = async (entityType, entity) => {
  const normalized = normalizeEntity(entity);
  if (normalized) {
    await cache.setCachedItem(entityType, normalized);
  }
  return normalized;
};

/**
 * Update cache after updating an entity
 */
export const cacheUpdate = async (entityType, entity) => {
  const normalized = normalizeEntity(entity);
  if (normalized) {
    await cache.setCachedItem(entityType, normalized);
  }
  return normalized;
};

/**
 * Update cache after deleting an entity
 */
export const cacheDelete = async (entityType, entityId) => {
  await cache.deleteCachedItem(entityType, entityId);
};

/**
 * Extract entity from API response
 * Handles different response formats
 */
export const extractEntityFromResponse = (response, entityKey = null) => {
  if (!response || !response.data) return null;

  // Check for nested data structure
  if (response.data.data) {
    if (entityKey && response.data.data[entityKey]) {
      return response.data.data[entityKey];
    }
    // Try common entity keys
    const keys = ['project', 'chapter', 'scene', 'note', 'corkboard', 'timeline'];
    const foundKey = keys.find((key) => response.data.data[key]);
    if (foundKey) {
      return response.data.data[foundKey];
    }
    // Return data.data if it's an object (single entity)
    if (typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
      return response.data.data;
    }
  }

  // Check for direct entity keys in data
  if (entityKey && response.data[entityKey]) {
    return response.data[entityKey];
  }

  // Try common entity keys
  const keys = ['project', 'chapter', 'scene', 'note', 'corkboard', 'timeline'];
  const foundKey = keys.find((key) => response.data[key]);
  if (foundKey) {
    return response.data[foundKey];
  }

  // Return data itself if it's an object
  if (typeof response.data === 'object' && !Array.isArray(response.data)) {
    return response.data;
  }

  return null;
};

/**
 * Extract entities array from API response
 */
export const extractEntitiesFromResponse = (response, entityKey = null) => {
  if (!response || !response.data) return [];

  // Check for nested data structure
  if (response.data.data) {
    if (entityKey && Array.isArray(response.data.data[entityKey])) {
      return response.data.data[entityKey];
    }
    // Try common entity keys (plural)
    const keys = ['projects', 'chapters', 'scenes', 'notes', 'corkboards', 'timelines'];
    const foundKey = keys.find((key) => Array.isArray(response.data.data[key]));
    if (foundKey) {
      return response.data.data[foundKey];
    }
    // Return data.data if it's an array
    if (Array.isArray(response.data.data)) {
      return response.data.data;
    }
  }

  // Check for direct entity keys in data
  const keys = ['projects', 'chapters', 'scenes', 'notes', 'corkboards', 'timelines'];
  const foundKey = keys.find((key) => Array.isArray(response.data[key]));
  if (foundKey) {
    return response.data[foundKey];
  }

  // Return data itself if it's an array
  if (Array.isArray(response.data)) {
    return response.data;
  }

  return [];
};

export default {
  cacheCreate,
  cacheUpdate,
  cacheDelete,
  extractEntityFromResponse,
  extractEntitiesFromResponse,
};
